import type { ApiClient } from '@treneo/infrastructure/api';
import { BaseApi } from '@treneo/infrastructure/api';
import type {
  ICreateCourseRequest,
  ICreateCourseResponse,
  IGetCourseForEditRequest,
  IGetCourseForEditResponse,
  IGetCourseJoinCodeRequest,
  IGetCourseJoinCodeResponse,
  IGetCourseRequest,
  IGetCourseResponse,
  IGetCoursesRequest,
  IGetCoursesResponse,
  IGetCourseStatusRequest,
  IGetCourseStatusResponse,
  IGetCourseTagsResponse,
  IGetFreshStartServersForCourseRequest,
  IGetFreshStartServersForCourseResponse,
  IGetOsTemplatesOfServerRequest,
  IGetOsTemplatesOfServerResponse,
  IGetTestCourseResponse,
  IJoinToCourseRequest,
  ISaveIsFreshStartMachineReadyToCloneRequest,
  ISaveMachineIframeSizeRequest,
  ISaveParticipantsMachineControlActiveRequest,
  ISaveShowOnlyActiveMachinesRequest,
  ISaveWhetherTrainerAllowedPreviewRequest,
  IUpdateCourseRequest,
  Result
} from '@treneo/models';

export class CoursesApi extends BaseApi {
  protected readonly baseUrl = '/api/courses';

  constructor(apiClient: ApiClient) {
    super(apiClient);
  }

  getCourses = (
    request: IGetCoursesRequest,
    signal?: AbortSignal
  ): Promise<Result<IGetCoursesResponse>> =>
    this.post<IGetCoursesResponse>('/search', request, 'coursesPage.loadError', signal);

  getCourse = (request: IGetCourseRequest): Promise<Result<IGetCourseResponse>> =>
    this.get<IGetCourseResponse>(`/${request.courseId}`, null, 'Get course failed');

  getCourseJoinCode = (
    request: IGetCourseJoinCodeRequest
  ): Promise<Result<IGetCourseJoinCodeResponse>> =>
    this.get<IGetCourseJoinCodeResponse>(
      `/${request.courseId}/join-code`,
      null,
      'Get course join code failed'
    );

  getCourseForEdit = (
    request: IGetCourseForEditRequest
  ): Promise<Result<IGetCourseForEditResponse>> =>
    this.get<IGetCourseForEditResponse>(
      `/${request.courseId}/for-edit`,
      null,
      'Get course for edit failed'
    );

  joinCourse = (request: IJoinToCourseRequest): Promise<Result<IGetCourseStatusResponse | null>> =>
    this.post<IGetCourseStatusResponse | null>('/joinToCourse', request, 'Join course failed');

  getCourseStatus = (request: IGetCourseStatusRequest): Promise<Result<IGetCourseStatusResponse>> =>
    this.get<IGetCourseStatusResponse>('/join/status', request, 'Get course status failed');

  getCourseTags = (
    signal?: AbortSignal,
    timeoutMs?: number
  ): Promise<Result<IGetCourseTagsResponse>> =>
    this.get<IGetCourseTagsResponse>('/tags', null, 'Get course tags failed', signal, timeoutMs);

  getTestCourse = (signal?: AbortSignal): Promise<Result<IGetTestCourseResponse>> =>
    this.get<IGetTestCourseResponse>('/test-course', null, 'Get test course failed', signal);

  saveMachineIframeSize = (request: ISaveMachineIframeSizeRequest): Promise<void> =>
    this.postVoid('/saveMachineIframeSize', request);

  getFreshStartServersForCourse = (
    request: IGetFreshStartServersForCourseRequest
  ): Promise<Result<IGetFreshStartServersForCourseResponse>> =>
    this.get<IGetFreshStartServersForCourseResponse>(
      '/getFreshStartServersForCourse',
      request,
      'Get fresh start servers failed'
    );

  getOsTemplatesOfServer = (
    request: IGetOsTemplatesOfServerRequest
  ): Promise<Result<IGetOsTemplatesOfServerResponse>> =>
    this.get<IGetOsTemplatesOfServerResponse>(
      '/getOsTemplatesOfServer',
      request,
      'Get OS templates failed'
    );

  createCourse = (request: ICreateCourseRequest): Promise<Result<ICreateCourseResponse>> =>
    this.post<ICreateCourseResponse>('', request, 'Create course failed');

  updateCourse = (request: IUpdateCourseRequest): Promise<void> =>
    this.putVoid(`/${request.courseId}`, request);

  saveIsFreshStartMachineReadyToClone = (
    request: ISaveIsFreshStartMachineReadyToCloneRequest
  ): Promise<void> => this.postVoid('/saveIsFreshStartMachineReadyToClone', request);

  saveWhetherTrainerAllowedPreview = (
    request: ISaveWhetherTrainerAllowedPreviewRequest
  ): Promise<void> => this.postVoid('/saveWhetherTrainerAllowedPreview', request);

  saveShowOnlyActiveMachines = (request: ISaveShowOnlyActiveMachinesRequest): Promise<void> =>
    this.postVoid('/saveShowOnlyActiveMachines', request);

  saveParticipantsMachineControlActive = (
    request: ISaveParticipantsMachineControlActiveRequest
  ): Promise<void> => this.postVoid('/saveParticipantsMachineControlActive', request);

  inviteTrainerToCourse = (request: {
    courseId: string;
    email: string;
    origin: string;
  }): Promise<Result<{ success: boolean; message: string }>> =>
    this.post<{ success: boolean; message: string }>(
      `/${request.courseId}/trainers/invite`,
      { email: request.email, origin: request.origin },
      'Invite trainer to course failed'
    );

  getCourseTrainerInvitations = (
    courseId: string
  ): Promise<Result<{ invitations: Array<{ email: string; [key: string]: unknown }> }>> =>
    this.get<{ invitations: Array<{ email: string; [key: string]: unknown }> }>(
      `/${courseId}/trainer-invitations`,
      null,
      'Get course trainer invitations failed'
    );

  cancelCourseTrainerInvitation = (request: {
    courseId: string;
    invitationId: string;
  }): Promise<void> =>
    this.deleteVoid(`/${request.courseId}/trainer-invitations/${request.invitationId}`);
}
